import React, { useState, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';
import { pushDataLayerEvent } from './TrackingScripts';
import PhoneLink, { PHONES } from './PhoneLink';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const SITUATIES = [
  { id: 'erfenis', label: 'Erfenis', full: 'Erfenis of nalatenschap', desc: 'VLABEL-schatting voor de erfbelasting', service: 'vlabel', badge: 'VLABEL' },
  { id: 'bank', label: 'Bank', full: 'Bank of hypotheek', desc: 'Schattingsverslag voor uw kredietdossier', service: 'schattingsverslag' },
  { id: 'echtscheiding', label: 'Echtscheiding', full: 'Echtscheiding of uit onverdeeldheid', desc: 'Neutraal verslag voor beide partijen', service: 'schattingsverslag' },
  { id: 'verkoop', label: 'Verkoop', full: 'Verkoop, aankoop of schenking', desc: 'Objectieve waardebepaling', service: 'schattingsverslag' },
  { id: 'verhuur', label: 'Verhuur', full: 'Verhuur: intrede of uittrede', desc: 'Plaatsbeschrijving met fotodossier', service: 'plaatsbeschrijving' },
];

export const SERVICE_LABELS = {
  vlabel: 'VLABEL-schatting',
  schattingsverslag: 'Schattingsverslag',
  plaatsbeschrijving: 'Plaatsbeschrijving',
};

const URGENTIES = [
  { id: 'Spoed', desc: 'Plaatsbezoek binnen 48 uur' },
  { id: 'Standaard', desc: 'Verslag binnen 5 werkdagen' },
];

export const isValidBelgianPhone = (v) => /^(\+32|0032|0)[1-9]\d{7,8}$/.test((v || '').replace(/[\s.\-/()]/g, ''));
const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || '').trim());

const SUBMIT_ERROR = 'Verzenden is niet gelukt. Probeer opnieuw of bel ons rechtstreeks: 0471 46 41 88.';

const inputClass = (hasError) =>
  `w-full min-h-[48px] border rounded-[var(--r-md)] px-4 py-3 text-[var(--ink)] bg-[var(--cream)] focus:bg-white transition-colors ${
    hasError ? 'border-red-500' : 'border-[var(--card-brd)]'
  }`;

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} role="alert" className="mt-1.5 text-sm text-red-600" data-testid={id}>
      {message}
    </p>
  ) : null;

const QuoteForm = () => {
  const [step, setStep] = useState(1);
  const [situatie, setSituatie] = useState('');
  const [urgentie, setUrgentie] = useState('Standaard');
  const [contact, setContact] = useState({ naam: '', telefoon: '', email: '', gemeente: '' });
  const [honeypot, setHoneypot] = useState('');
  const [step1Error, setStep1Error] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const startedRef = useRef(false);
  const refs = { naam: useRef(null), telefoon: useRef(null), email: useRef(null) };

  const sit = SITUATIES.find((s) => s.id === situatie);

  const trackStart = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    pushDataLayerEvent('offerte_stap1_gestart');
  };

  const chooseSituatie = (id) => {
    trackStart();
    setSituatie(id);
    setStep1Error('');
  };

  const chooseUrgentie = (id) => {
    trackStart();
    setUrgentie(id);
  };

  const goToStep2 = () => {
    if (!sit) {
      setStep1Error('Kies de situatie die het best past — dan weten we welk verslag u nodig heeft.');
      return;
    }
    setStep(2);
    pushDataLayerEvent('offerte_stap2_bereikt', { situatie: sit.full, dienst: SERVICE_LABELS[sit.service], urgentie });
  };

  const updateContact = (field, value) => {
    setContact((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (submitError) setSubmitError('');
  };

  const validateContact = () => {
    const next = {};
    if (!contact.naam.trim()) next.naam = 'Vul uw naam in.';
    if (!isValidBelgianPhone(contact.telefoon)) next.telefoon = 'Vul een geldig Belgisch nummer in, bv. 0470 12 34 56.';
    if (!isValidEmail(contact.email)) next.email = 'Controleer uw e-mailadres.';
    setErrors(next);
    const first = ['naam', 'telefoon', 'email'].find((f) => next[f]);
    if (first) refs[first].current?.focus();
    return !first;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || !validateContact()) return;
    setIsSubmitting(true);
    setSubmitError('');

    const naam = contact.naam.trim();
    const [voornaam, ...rest] = naam.split(/\s+/);
    const gemeente = contact.gemeente.trim();
    const dienst = SERVICE_LABELS[sit.service];

    try {
      const leadResponse = await axios.post(`${API}/leads`, {
        service_type: sit.service,
        situation: sit.full,
        urgency: urgentie,
        first_name: voornaam,
        last_name: rest.join(' '),
        email: contact.email.trim(),
        phone: contact.telefoon.trim(),
        city: gemeente,
        postal_code: (gemeente.match(/\b\d{4}\b/) || [''])[0],
        address: '',
        reason: sit.full,
        property_type: '',
        notes: `Urgentie: ${urgentie}`,
        honeypot,
      });

      sessionStorage.setItem('bedankt_meta', JSON.stringify({ naam: voornaam, situatie: sit.full, urgentie }));
      sessionStorage.setItem(
        'conversion_lead',
        JSON.stringify({
          lead_id: leadResponse.data?.id || '',
          form_type: 'offerte_aanvraag',
          service_type: sit.service,
          situatie: sit.full,
          urgentie,
          reason: sit.full,
          timing: urgentie,
          property_type: '',
        })
      );

      try {
        const tokenResponse = await axios.post(`${API}/conversion/create-token`);
        sessionStorage.setItem('conversion_token', tokenResponse.data.token);
      } catch (tokenError) {
        if (process.env.NODE_ENV === 'development') console.error('Conversion token failed:', tokenError);
      }

      pushDataLayerEvent('offerte_verzonden', { situatie: sit.full, dienst, urgentie, gemeente: gemeente || 'onbekend' });
      setTimeout(() => {
        window.location.href = '/bedankt';
      }, 250);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Lead submit failed:', error);
      setSubmitError(SUBMIT_ERROR);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-[var(--r-xl)] shadow-lg overflow-hidden" id="aanvraag" data-testid="quote-form">
      <div className="px-6 py-4 border-b border-[var(--card-brd)] text-center">
        <p className="text-[var(--ink-md)] text-sm">Gratis & vrijblijvend</p>
        <h3 className="font-display text-2xl text-[var(--ink)]">Offerte aanvragen</h3>
      </div>

      <div className="h-1 bg-[var(--cream-dk)]" aria-hidden="true">
        <div
          className="h-full bg-[var(--amber)] transition-[width] duration-500 ease-out"
          style={{ width: step === 2 ? '50%' : '0%' }}
          data-testid="quote-progress"
        />
      </div>

      <div className="p-5 sm:p-6">
        {step === 1 ? (
          <StepOne
            situatie={situatie}
            urgentie={urgentie}
            step1Error={step1Error}
            onSituatie={chooseSituatie}
            onUrgentie={chooseUrgentie}
            onNext={goToStep2}
          />
        ) : (
          <StepTwo
            sit={sit}
            urgentie={urgentie}
            contact={contact}
            errors={errors}
            refs={refs}
            honeypot={honeypot}
            setHoneypot={setHoneypot}
            submitError={submitError}
            isSubmitting={isSubmitting}
            onChange={updateContact}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
          />
        )}
      </div>
    </div>
  );
};

const StepOne = ({ situatie, urgentie, step1Error, onSituatie, onUrgentie, onNext }) => (
  <div className="space-y-5">
    <p className="text-xs font-semibold text-[var(--ink-lt)] uppercase tracking-wide">Stap 1 van 2 — Uw situatie</p>

    <fieldset>
      <legend className="block text-sm font-medium text-[var(--ink)] mb-3">Waarvoor heeft u het verslag nodig?</legend>
      <div className="space-y-2">
        {SITUATIES.map((s) => {
          const active = situatie === s.id;
          return (
            <label key={s.id} className={`quote-card ${active ? 'active' : ''}`} data-testid={`situatie-${s.id}`}>
              <input
                type="radio"
                name="situatie"
                value={s.id}
                checked={active}
                onChange={() => onSituatie(s.id)}
                className="sr-only"
              />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-[var(--ink)]">{s.full}</p>
                  <p className="text-xs text-[var(--ink-lt)]">{s.desc}</p>
                </div>
                {s.badge ? (
                  <span className="vlabel-badge flex-shrink-0">{s.badge}</span>
                ) : (
                  active && <CheckCircle2 className="w-5 h-5 text-[var(--amber)] flex-shrink-0" />
                )}
              </div>
            </label>
          );
        })}
      </div>
      <FieldError id="situatie-error" message={step1Error} />
    </fieldset>

    <fieldset>
      <legend className="block text-sm font-medium text-[var(--ink)] mb-3">Hoe snel heeft u het nodig?</legend>
      <div className="quote-grid-2">
        {URGENTIES.map((u) => {
          const active = urgentie === u.id;
          return (
            <label key={u.id} className={`quote-card ${active ? 'active' : ''}`} data-testid={`urgentie-${u.id.toLowerCase()}`}>
              <input type="radio" name="urgentie" value={u.id} checked={active} onChange={() => onUrgentie(u.id)} className="sr-only" />
              <p className="font-medium text-[var(--ink)]">{u.id}</p>
              <p className="text-xs text-[var(--ink-lt)]">{u.desc}</p>
            </label>
          );
        })}
      </div>
    </fieldset>

    <button type="button" onClick={onNext} className="btn-primary w-full min-h-[48px]" data-testid="quote-next-button">
      Volgende ›
    </button>
    <p className="text-xs text-[var(--ink-xs)] text-center">Nog één stap: uw naam en nummer.</p>
  </div>
);

const StepTwo = ({ sit, urgentie, contact, errors, refs, honeypot, setHoneypot, submitError, isSubmitting, onChange, onBack, onSubmit }) => (
  <form onSubmit={onSubmit} noValidate className="space-y-5">
    <p className="text-xs font-semibold text-[var(--ink-lt)] uppercase tracking-wide">Stap 2 van 2 — Hoe bereiken we u?</p>

    <div className="bg-[var(--cream)] rounded-[var(--r-md)] px-4 py-3 flex items-center justify-between gap-3 text-sm" data-testid="quote-summary">
      <span className="text-[var(--ink)]">
        {sit.label} · {SERVICE_LABELS[sit.service]} · {urgentie}
      </span>
      <button type="button" onClick={onBack} className="text-[var(--amber)] underline underline-offset-2 hover:text-[var(--amber-lt)]" data-testid="quote-edit-link">
        wijzig
      </button>
    </div>

    <div>
      <label htmlFor="quote-naam" className="block text-sm font-medium text-[var(--ink)] mb-2">Uw naam</label>
      <input
        id="quote-naam"
        ref={refs.naam}
        type="text"
        autoComplete="name"
        value={contact.naam}
        onChange={(e) => onChange('naam', e.target.value)}
        placeholder="Voornaam Achternaam"
        aria-invalid={!!errors.naam}
        aria-describedby={errors.naam ? 'naam-error' : undefined}
        className={inputClass(errors.naam)}
        data-testid="quote-naam-input"
      />
      <FieldError id="naam-error" message={errors.naam} />
    </div>

    <div>
      <label htmlFor="quote-telefoon" className="block text-sm font-medium text-[var(--ink)] mb-2">Telefoonnummer</label>
      <input
        id="quote-telefoon"
        ref={refs.telefoon}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={contact.telefoon}
        onChange={(e) => onChange('telefoon', e.target.value)}
        placeholder="0470 12 34 56"
        aria-invalid={!!errors.telefoon}
        aria-describedby={errors.telefoon ? 'telefoon-error' : undefined}
        className={inputClass(errors.telefoon)}
        data-testid="quote-telefoon-input"
      />
      <FieldError id="telefoon-error" message={errors.telefoon} />
    </div>

    <div className="quote-grid-2">
      <div>
        <label htmlFor="quote-email" className="block text-sm font-medium text-[var(--ink)] mb-2">E-mail (voor de offerte)</label>
        <input
          id="quote-email"
          ref={refs.email}
          type="email"
          autoComplete="email"
          value={contact.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="uw@email.be"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? 'email-error' : undefined}
          className={inputClass(errors.email)}
          data-testid="quote-email-input"
        />
        <FieldError id="email-error" message={errors.email} />
      </div>
      <div>
        <label htmlFor="quote-gemeente" className="block text-sm font-medium text-[var(--ink)] mb-2">Gemeente van het pand <span className="text-[var(--ink-xs)] font-normal">(optioneel)</span></label>
        <input
          id="quote-gemeente"
          type="text"
          autoComplete="address-level2"
          value={contact.gemeente}
          onChange={(e) => onChange('gemeente', e.target.value)}
          placeholder="bv. Gent"
          className={inputClass(false)}
          data-testid="quote-gemeente-input"
        />
      </div>
    </div>

    <div className="sr-only" aria-hidden="true">
      <label htmlFor="website">Website</label>
      <input type="text" id="website" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" />
    </div>

    {submitError && (
      <p role="alert" className="text-sm text-red-600" data-testid="quote-submit-error">{submitError}</p>
    )}

    <div className="flex gap-3">
      <button type="button" onClick={onBack} className="btn-secondary min-h-[48px] !bg-white !px-4" data-testid="quote-back-button">
        ‹ Terug
      </button>
      <button type="submit" disabled={isSubmitting} className="btn-primary flex-1 min-h-[48px] disabled:opacity-70 disabled:cursor-not-allowed" data-testid="quote-submit-button">
        {isSubmitting ? 'Even geduld…' : 'Offerte aanvragen'}
      </button>
    </div>

    <p className="text-xs text-[var(--ink-xs)] text-center">Gino of Bjorn reageert persoonlijk binnen 24 uur. Geen verplichtingen.</p>
  </form>
);

export const PhoneStrip = () => (
  <div className="mt-4 bg-[var(--cream)] rounded-[var(--r-md)] px-4 py-3 text-sm text-[var(--ink-md)] text-center" data-testid="phone-strip">
    Liever meteen iemand aan de lijn?{' '}
    <span className="whitespace-nowrap">
      Gino <PhoneLink phone={PHONES.gino} locatie="hero" className="font-semibold text-[var(--ink)] hover:text-[var(--amber)] transition-colors" />
    </span>
    {' · '}
    <span className="whitespace-nowrap">
      Bjorn <PhoneLink phone={PHONES.bjorn} locatie="hero" className="font-semibold text-[var(--ink)] hover:text-[var(--amber)] transition-colors" />
    </span>
  </div>
);

export default QuoteForm;
